// Implements: F-MCK-006, F-MCK-015, F-MCK-016, F-MCK-017, F-MCK-019, F-UIX-009
/**
 * The mutations of the gallery, the trash and the mockup header.
 *
 * None of them is optimistic: the card changes when the server says it did
 * (the cache is invalidated, so the list is refetched), which is what makes a
 * refusal leave the card exactly where it was. Each action answers `true` or
 * `false` and never rejects, because the container that awaits it has only one
 * decision left to make: which notice to show.
 *
 * Mockup v3 P8: every write that moves a mockup between gallery tabs (favourite, trash, restore,
 * delete for good, change of scope) also invalidates the tab counts (`GALLERY_COUNTS_KEY`).
 */

import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import type { ApiError } from "../../../shared/api/ApiError";
import { asApiError } from "../../../shared/api/asApiError";
import { GALLERY_COUNTS_KEY, MOCKUPS_KEY, MOCKUPS_TRASH_KEY, mockupDetailKey } from "../../../shared/api/queryKeys";
import {
  addMockupTag,
  changeMockupScope,
  deleteMockupPermanently,
  removeMockupTag,
  renameMockup,
  restoreMockup,
  setMockupFavorite,
  trashMockup,
} from "../api/mockupsApi";
import type { MockupDetailView } from "../../../types/api";

/** The refusals after which the mockup is read again: its role or its existence changed. */
const STANDING_CHANGED: ReadonlySet<number> = new Set([403, 404]);

export interface UseMockupActionsResult {
  readonly setFavorite: (mockupId: string, isFavorite: boolean) => Promise<boolean>;
  readonly restore: (mockupId: string) => Promise<boolean>;
  readonly deleteForever: (mockupId: string) => Promise<boolean>;
  /** Move to the trash (restorable): the gallery and the trash both change. */
  readonly trash: (mockupId: string) => Promise<boolean>;
  readonly changeScope: (
    mockupId: string,
    scopeKind: string,
    scopeId: string | null,
  ) => Promise<ApiError | null>;
  /** The header's edits answer the refusal itself (or `null`), as `changeScope` does. */
  readonly rename: (mockupId: string, name: string) => Promise<ApiError | null>;
  readonly addTag: (mockupId: string, tag: string) => Promise<ApiError | null>;
  readonly removeTag: (mockupId: string, tag: string) => Promise<ApiError | null>;
  /**
   * The person's role on the mockup as last read from the server (`undefined` when it is not
   * read). A header edit answers only after the refetch its refusal triggers, so right after a
   * `403` this already says whether the person may still edit.
   */
  readonly currentRole: (mockupId: string) => string | undefined;
}

export function useMockupActions(): UseMockupActionsResult {
  const queryClient = useQueryClient();

  const setFavorite = useCallback(
    async (mockupId: string, isFavorite: boolean): Promise<boolean> => {
      try {
        await setMockupFavorite(mockupId, isFavorite);
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: MOCKUPS_KEY }),
          queryClient.invalidateQueries({ queryKey: GALLERY_COUNTS_KEY }),
        ]);
        return true;
      } catch {
        return false;
      }
    },
    [queryClient],
  );

  const restore = useCallback(
    async (mockupId: string): Promise<boolean> => {
      try {
        await restoreMockup(mockupId);
        // A restored mockup leaves the trash AND joins the gallery.
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: MOCKUPS_TRASH_KEY }),
          queryClient.invalidateQueries({ queryKey: MOCKUPS_KEY }),
          queryClient.invalidateQueries({ queryKey: GALLERY_COUNTS_KEY }),
        ]);
        return true;
      } catch {
        return false;
      }
    },
    [queryClient],
  );

  const deleteForever = useCallback(
    async (mockupId: string): Promise<boolean> => {
      const reread = (): Promise<unknown> =>
        Promise.all([
          queryClient.invalidateQueries({ queryKey: MOCKUPS_TRASH_KEY }),
          queryClient.invalidateQueries({ queryKey: GALLERY_COUNTS_KEY }),
        ]);
      try {
        await deleteMockupPermanently(mockupId);
      } catch (cause) {
        // A `404` means the row is already gone (a second click, or the daily purge got there
        // first): read the trash again so it stops showing a row the server no longer has.
        if (STANDING_CHANGED.has(asApiError(cause).status)) await reread();
        return false;
      }
      await reread();
      return true;
    },
    [queryClient],
  );

  const trash = useCallback(
    async (mockupId: string): Promise<boolean> => {
      try {
        await trashMockup(mockupId);
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: MOCKUPS_KEY }),
          queryClient.invalidateQueries({ queryKey: MOCKUPS_TRASH_KEY }),
          queryClient.invalidateQueries({ queryKey: GALLERY_COUNTS_KEY }),
        ]);
        return true;
      } catch {
        return false;
      }
    },
    [queryClient],
  );

  const changeScope = useCallback(
    async (
      mockupId: string,
      scopeKind: string,
      scopeId: string | null,
    ): Promise<ApiError | null> => {
      try {
        await changeMockupScope(mockupId, scopeKind, scopeId);
      } catch (cause) {
        // The refusal itself (403, 422...) is what the person needs to read.
        return asApiError(cause);
      }
      // The scope is shown from the server: refetch it, and the gallery too,
      // because a scope change can move a card out of the visible set.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: mockupDetailKey(mockupId) }),
        queryClient.invalidateQueries({ queryKey: MOCKUPS_KEY }),
        queryClient.invalidateQueries({ queryKey: GALLERY_COUNTS_KEY }),
      ]);
      return null;
    },
    [queryClient],
  );

  /**
   * One edit of the mockup header: the write, then the mockup and the gallery are read again,
   * because both show the name and the tags and neither is patched by hand.
   *
   * A refusal that says the person's standing changed (`403`: the role was taken away; `404`: the
   * mockup is gone or no longer theirs) reads the mockup again too, so the page stops offering
   * what the server will refuse. Any other refusal changed nothing on the server.
   */
  const edit = useCallback(
    async (mockupId: string, write: () => Promise<unknown>): Promise<ApiError | null> => {
      try {
        await write();
      } catch (cause) {
        const refusal = asApiError(cause);
        if (STANDING_CHANGED.has(refusal.status)) {
          await queryClient.invalidateQueries({ queryKey: mockupDetailKey(mockupId) });
        }
        return refusal;
      }
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: mockupDetailKey(mockupId) }),
        queryClient.invalidateQueries({ queryKey: MOCKUPS_KEY }),
      ]);
      return null;
    },
    [queryClient],
  );

  const rename = useCallback(
    (mockupId: string, name: string) => edit(mockupId, () => renameMockup(mockupId, name)),
    [edit],
  );
  const addTag = useCallback(
    (mockupId: string, tag: string) => edit(mockupId, () => addMockupTag(mockupId, tag)),
    [edit],
  );
  const removeTag = useCallback(
    (mockupId: string, tag: string) => edit(mockupId, () => removeMockupTag(mockupId, tag)),
    [edit],
  );

  const currentRole = useCallback(
    (mockupId: string) => queryClient.getQueryData<MockupDetailView>(mockupDetailKey(mockupId))?.your_role,
    [queryClient],
  );

  return { setFavorite, restore, deleteForever, trash, changeScope, rename, addTag, removeTag, currentRole };
}
